import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, ShoppingCart } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Logo } from '../Logo';
import { useCart } from '../../context/CartContext';

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { count, openCart } = useCart();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Chi Siamo', path: '/chi-siamo' },
    { name: 'Negozio', path: '/negozio' },
    { name: 'Galleria', path: '/galleria' },
    { name: 'News', path: '/news' },
    { name: 'Recensioni', path: '/recensioni' },
    { name: 'Contatti', path: '/contatti' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        scrolled ? 'glass-nav shadow-card border-b border-primary/10' : 'bg-transparent'
      }`}
    >
      <div className="container-custom">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Logo isLight={!scrolled} />

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.path.replace('/', '') || 'home'}`}
                className={`font-body text-sm font-medium transition-colors duration-300 link-underline ${
                  isActive(link.path)
                    ? 'text-primary'
                    : scrolled
                    ? 'text-text-DEFAULT hover:text-primary'
                    : 'text-white hover:text-accent'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* Cart Button */}
          <button
            onClick={openCart}
            aria-label="Apri carrello"
            className={`hidden md:flex relative items-center justify-center w-10 h-10 rounded-full transition-all duration-300 ${
              scrolled ? 'text-text-DEFAULT hover:text-primary' : 'text-white hover:text-accent'
            }`}
          >
            <ShoppingCart className="w-5 h-5" />
            {count > 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-accent text-primary-dark text-xs font-bold rounded-full flex items-center justify-center leading-none">
                {count > 9 ? '9+' : count}
              </span>
            )}
          </button>

          {/* Phone CTA */}
          <a
            href="tel:+393382523925"
            data-testid="phone-cta"
            className={`hidden md:flex items-center space-x-2 px-5 py-2.5 rounded-full font-body text-sm font-medium transition-all duration-300 ${
              scrolled
                ? 'bg-primary text-white hover:bg-primary-dark'
                : 'bg-white/20 backdrop-blur-sm text-white hover:bg-white/30'
            }`}
          >
            <Phone className="w-4 h-4" />
            <span>Chiamaci</span>
          </a>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-btn"
            className={`lg:hidden p-2 rounded-lg transition-colors ${
              scrolled ? 'text-primary' : 'text-white'
            }`}
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t border-primary/10"
            data-testid="mobile-menu"
          >
            <div className="container-custom py-4 space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`mobile-nav-link-${link.path.replace('/', '') || 'home'}`}
                  className={`block py-3 px-4 rounded-lg font-body text-base transition-colors ${
                    isActive(link.path)
                      ? 'bg-primary/10 text-primary font-medium'
                      : 'text-text-DEFAULT hover:bg-background-paper'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <a
                href="tel:+393382523925"
                className="flex items-center justify-center space-x-2 mt-4 py-3 bg-primary text-white rounded-full font-body font-medium"
              >
                <Phone className="w-4 h-4" />
                <span>+39 338 252 3925</span>
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};
